import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Bot, 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  DollarSign, 
  Settings,
  Pause,
  Play,
  Trash2,
  Zap,
  Target,
  BarChart3
} from 'lucide-react';
import { CreateBotModal } from './CreateBotModal';
import { BotDetailsModal } from './BotDetailsModal';
import { toast } from 'sonner';

export interface TradingBot {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'stopped';
  strategy: 'scalping' | 'swing' | 'trend' | 'grid' | 'arbitrage';
  pair: string;
  investment: number;
  profit: number;
  profitPercent: number;
  trades: number;
  winRate: number;
  createdAt: Date;
  lastTrade?: Date;
  config: {
    buyThreshold: number; // % de caída para comprar
    sellThreshold: number; // % de subida para vender
    stopLoss: number; // % de pérdida máxima
    takeProfit: number; // % de ganancia objetivo
    maxTradesPerDay: number;
    tradeAmount: number; // Monto por operación
  };
  performance: {
    totalTrades: number;
    winningTrades: number;
    losingTrades: number;
    avgProfit: number;
    bestTrade: number;
    worstTrade: number;
  };
}

export function TradingBotsManager() {
  const [bots, setBots] = useState<TradingBot[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedBot, setSelectedBot] = useState<TradingBot | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Simulación de trading en tiempo real
  useEffect(() => {
    const interval = setInterval(() => {
      setBots(prevBots => 
        prevBots.map(bot => {
          if (bot.status !== 'active') return bot;

          // Simular cambios de precio aleatorios
          const priceChange = (Math.random() - 0.5) * 5; // -2.5% a +2.5%
          
          // Lógica de compra/venta automática
          let newProfit = bot.profit;
          let newTrades = bot.performance.totalTrades;
          let winningTrades = bot.performance.winningTrades;
          let losingTrades = bot.performance.losingTrades;

          // Compra si el precio baja más del threshold
          if (priceChange < -bot.config.buyThreshold) {
            // Simular ganancia de la operación
            const tradeProfit = Math.random() * bot.config.tradeAmount * 0.05; // 0-5% ganancia
            const isWin = Math.random() > 0.4; // 60% win rate
            
            if (isWin) {
              newProfit += tradeProfit;
              winningTrades++;
            } else {
              newProfit -= tradeProfit * 0.5;
              losingTrades++;
            }
            
            newTrades++;

            toast.success(`🤖 ${bot.name} ejecutó COMPRA`, {
              description: `Par: ${bot.pair} | ${isWin ? 'Ganancia' : 'Pérdida'}: $${tradeProfit.toFixed(2)}`
            });
          }
          
          // Vende si el precio sube más del threshold
          if (priceChange > bot.config.sellThreshold) {
            const tradeProfit = Math.random() * bot.config.tradeAmount * 0.05;
            const isWin = Math.random() > 0.4;
            
            if (isWin) {
              newProfit += tradeProfit;
              winningTrades++;
            } else {
              newProfit -= tradeProfit * 0.5;
              losingTrades++;
            }
            
            newTrades++;

            toast.success(`🤖 ${bot.name} ejecutó VENTA`, {
              description: `Par: ${bot.pair} | ${isWin ? 'Ganancia' : 'Pérdida'}: $${tradeProfit.toFixed(2)}`
            });
          }

          const newWinRate = newTrades > 0 ? (winningTrades / newTrades) * 100 : 0;
          const newProfitPercent = bot.investment > 0 ? (newProfit / bot.investment) * 100 : 0;

          return {
            ...bot,
            profit: newProfit,
            profitPercent: newProfitPercent,
            trades: newTrades,
            winRate: newWinRate,
            lastTrade: newTrades > bot.performance.totalTrades ? new Date() : bot.lastTrade,
            performance: {
              ...bot.performance,
              totalTrades: newTrades,
              winningTrades,
              losingTrades,
              avgProfit: newTrades > 0 ? newProfit / newTrades : 0,
            }
          };
        })
      );
    }, 8000); // Chequear cada 8 segundos

    return () => clearInterval(interval);
  }, []);

  const handleCreateBot = (botData: Partial<TradingBot>) => {
    const newBot: TradingBot = {
      id: Date.now().toString(),
      name: botData.name || 'Nuevo Bot',
      status: 'active',
      strategy: botData.strategy || 'scalping',
      pair: botData.pair || 'BTC/USDT',
      investment: botData.investment || 1000,
      profit: 0,
      profitPercent: 0,
      trades: 0,
      winRate: 0,
      createdAt: new Date(),
      config: botData.config || {
        buyThreshold: 1.5,
        sellThreshold: 1.5,
        stopLoss: 5,
        takeProfit: 10,
        maxTradesPerDay: 20,
        tradeAmount: 100,
      },
      performance: {
        totalTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        avgProfit: 0,
        bestTrade: 0,
        worstTrade: 0,
      }
    };

    setBots([...bots, newBot]);
    toast.success('Bot creado exitosamente', {
      description: `${newBot.name} está ahora activo y operando`
    });
  };

  const toggleBotStatus = (botId: string) => {
    setBots(prevBots =>
      prevBots.map(bot => {
        if (bot.id === botId) {
          const newStatus = bot.status === 'active' ? 'paused' : 'active';
          toast.info(`Bot ${newStatus === 'active' ? 'activado' : 'pausado'}`, {
            description: bot.name
          });
          return { ...bot, status: newStatus };
        }
        return bot;
      })
    );
  };

  const deleteBot = (botId: string) => {
    const bot = bots.find(b => b.id === botId);
    setBots(prevBots => prevBots.filter(b => b.id !== botId));
    toast.success('Bot eliminado', {
      description: bot?.name
    });
  };

  const getStrategyInfo = (strategy: string) => {
    const strategies = {
      scalping: { label: 'Scalping', color: 'bg-blue-500', icon: Zap },
      swing: { label: 'Swing', color: 'bg-purple-500', icon: Activity },
      trend: { label: 'Tendencia', color: 'bg-green-500', icon: TrendingUp },
      grid: { label: 'Grid', color: 'bg-orange-500', icon: Target },
      arbitrage: { label: 'Arbitraje', color: 'bg-pink-500', icon: BarChart3 },
    };
    return strategies[strategy as keyof typeof strategies] || strategies.scalping;
  };

  const totalProfit = bots.reduce((sum, bot) => sum + bot.profit, 0);
  const totalInvestment = bots.reduce((sum, bot) => sum + bot.investment, 0);
  const totalProfitPercent = totalInvestment > 0 ? (totalProfit / totalInvestment) * 100 : 0;
  const activeBots = bots.filter(b => b.status === 'active').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-white mb-2">Trading Bots</h1>
          <p className="text-gray-400">Gestiona tus bots de trading automático</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Crear Bot
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#0a0a0a] border-violet-500/20 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Total Bots</span>
            <Bot className="w-5 h-5 text-violet-400" />
          </div>
          <div className="text-2xl text-white">{bots.length}</div>
          <div className="text-xs text-gray-500 mt-1">{activeBots} activos</div>
        </Card>

        <Card className="bg-[#0a0a0a] border-violet-500/20 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Inversión Total</span>
            <DollarSign className="w-5 h-5 text-blue-400" />
          </div>
          <div className="text-2xl text-white">${totalInvestment.toLocaleString()}</div>
        </Card>

        <Card className="bg-[#0a0a0a] border-violet-500/20 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Ganancia Total</span>
            {totalProfit >= 0 ? (
              <TrendingUp className="w-5 h-5 text-green-400" />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-400" />
            )}
          </div>
          <div className={`text-2xl ${totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            ${totalProfit.toFixed(2)}
          </div>
          <div className={`text-xs mt-1 ${totalProfitPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {totalProfitPercent >= 0 ? '+' : ''}{totalProfitPercent.toFixed(2)}%
          </div>
        </Card>

        <Card className="bg-[#0a0a0a] border-violet-500/20 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Total Operaciones</span>
            <Activity className="w-5 h-5 text-purple-400" />
          </div>
          <div className="text-2xl text-white">
            {bots.reduce((sum, bot) => sum + bot.trades, 0)}
          </div>
        </Card>
      </div>

      {/* Bots List */}
      {bots.length === 0 ? (
        <Card className="bg-[#0a0a0a] border-violet-500/20 p-12">
          <div className="text-center">
            <Bot className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No hay bots creados</h3>
            <p className="text-gray-400 mb-6">Crea tu primer bot de trading automático</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear Primer Bot
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {bots.map(bot => {
            const strategyInfo = getStrategyInfo(bot.strategy);
            const StrategyIcon = strategyInfo.icon;

            return (
              <Card key={bot.id} className="bg-[#0a0a0a] border-violet-500/20 p-6 hover:border-violet-500/40 transition-all">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg ${strategyInfo.color} p-2.5 shadow-lg`}>
                      <StrategyIcon className="w-full h-full text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{bot.name}</h3>
                      <p className="text-sm text-gray-400">{bot.pair}</p>
                    </div>
                  </div>
                  <Badge
                    variant={bot.status === 'active' ? 'default' : 'secondary'}
                    className={
                      bot.status === 'active'
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                    }
                  >
                    {bot.status === 'active' ? 'Activo' : 'Pausado'}
                  </Badge>
                </div>

                {/* Stats */}
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Inversión</span>
                    <span className="text-white">${bot.investment.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Ganancia</span>
                    <span className={bot.profit >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {bot.profit >= 0 ? '+' : ''}${bot.profit.toFixed(2)} ({bot.profitPercent.toFixed(2)}%)
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Operaciones</span>
                    <span className="text-white">{bot.trades}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Win Rate</span>
                    <span className="text-white">{bot.winRate.toFixed(1)}%</span>
                  </div>
                </div>

                {/* Strategy Badge */}
                <div className="mb-4">
                  <Badge variant="outline" className="border-violet-500/30 text-violet-400">
                    {strategyInfo.label}
                  </Badge>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleBotStatus(bot.id)}
                    className="flex-1 border-violet-500/30 hover:bg-violet-500/10"
                  >
                    {bot.status === 'active' ? (
                      <><Pause className="w-4 h-4 mr-2" /> Pausar</>
                    ) : (
                      <><Play className="w-4 h-4 mr-2" /> Activar</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedBot(bot);
                      setShowDetailsModal(true);
                    }}
                    className="flex-1 border-violet-500/30 hover:bg-violet-500/10"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Detalles
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteBot(bot.id)}
                    className="border-red-500/30 hover:bg-red-500/10 text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <CreateBotModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateBot={handleCreateBot}
      />

      {selectedBot && (
        <BotDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedBot(null);
          }}
          bot={selectedBot}
        />
      )}
    </div>
  );
}
